import { AppShell, Divider, NavLink } from '@mantine/core';
import { IconDownload } from '@tabler/icons-react';
import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import { useParams, useSearchParams } from 'react-router-dom';
import { Show } from '../../../../../react-commons/components/control-flow/Show';
import { OpenModal } from '../../../../../react-commons/utils/modals/openModal';
import { setImportingOrExportingState, setTaskRefresh } from '../../../persisted/global/globalActions';
import { CriteriaDirection } from '../../../resource/enumerations/CriteriaDirection';
import { ProcessArtifactDirection } from '../../../resource/enumerations/ProcessArtifactDirection';
import { useProcessAreaActions } from '../../../resource/resources/artifacts/process_area/processAreaResource';
import { useProjectVersionPublishmentActions } from '../../../resource/resources/artifacts/project/projectVersionPublishmentResource';
import { useProjectVersionActions } from '../../../resource/resources/artifacts/project/projectVersionResource';
import { useUserActions } from '../../../resource/resources/persistables/user/userResource';
import { startDownloadPVT, startDownloadRichText } from '../../../resource/services/system/systemActions';
import { useThunkDispatch } from '../../../store/rootReducer';
import { useUserId } from '../../authentication/useUserId';
import { StackedActivitiesView } from '../../page-components/process-area/activity/StackedActivitiesView';
import { StackedCriteriasView } from '../../page-components/process-area/criteria/StackedCriteriasView';
import { StackedMethodsView } from '../../page-components/process-area/method/StackedMethodsView';
import { StackedObjectivesView } from '../../page-components/process-area/objective/StackedObjectivesView';
import { StackedProcessArtifactsView } from '../../page-components/process-area/process-artifact/StackedProcessArtifactView';
import { StackedRolesView } from '../../page-components/process-area/role/StackedRolesView';
import { StackedToolsView } from '../../page-components/process-area/tool/StackedToolsView';
import { StackedVerificationMetricsView } from '../../page-components/process-area/verification-metrics/StackedVerificationMetricsView';
import { ConditionalComponent } from '../../reusable-components/ConditionalComponent';

export enum MenuNodeTypes {
    ACTIVITIES = 'ACTIVITIES',
    TOOLS = 'TOOLS',
    VERIFICATION_METRICS = 'VERIFICATION_METRICS',
    INPUTS = 'INPUTS',
    OBJECTIVES = 'OBJECTIVES',
    OUTPUTS = 'OUTPUTS',
    ROLES = 'ROLES',
    METHODS = 'METHODS',
    ENTRY_CRITERIA = 'ENTRY_CRITERIA',
    EXIT_CRITERIA = 'EXIT_CRITERIA',
}

export const ProcessAreaComponent = () => {
    const userId = useUserId('');
    const isAuthenticated = Boolean(userId);

    const dispatch = useDispatch();
    const thunkDispatch = useThunkDispatch();

    const {
        id: projectId,
        versionId,
        processAreaId,
        processAreaObjectId,
    } = useParams<{
        id: string;
        versionId: string;
        processAreaId: string;
        processAreaObjectId: string;
    }>();
    const [search, setSearch] = useSearchParams();

    const [selectedNode, setSelectedNode] = useState<MenuNodeTypes | undefined>(
        search.get('section') === 'objectives' ? MenuNodeTypes.OBJECTIVES
        : search.get('section') === 'entry-criteria' ? MenuNodeTypes.ENTRY_CRITERIA
        : search.get('section') === 'inputs' ? MenuNodeTypes.INPUTS
        : search.get('section') === 'outputs' ? MenuNodeTypes.OUTPUTS
        : search.get('section') === 'roles' ? MenuNodeTypes.ROLES
        : search.get('section') === 'methods' ? MenuNodeTypes.METHODS
        : search.get('section') === 'activities' ? MenuNodeTypes.ACTIVITIES
        : search.get('section') === 'tools' ? MenuNodeTypes.TOOLS
        : search.get('section') === 'verification-metrics' ? MenuNodeTypes.VERIFICATION_METRICS
        : search.get('section') === 'exit-criteria' ? MenuNodeTypes.EXIT_CRITERIA
        : MenuNodeTypes.ACTIVITIES
    );

    const userActions = useUserActions();
    const projectVersionActions = useProjectVersionActions();
    const projectVersionPublishmentActions = useProjectVersionPublishmentActions();
    const processAreaActions = useProcessAreaActions();
    const projectVersionQuery = projectVersionActions.useGetByPublishment(search.get('publishment'), projectId);
    const projectVersionPublishmentQuery = projectVersionPublishmentActions.useGetSingleResource(
        versionId ?? projectVersionQuery.data?.id
    );
    const processAreaByIdQuery = processAreaActions.useGetResource(processAreaId);
    const processAreaByObjectIdQuery = processAreaActions.useGetResourceByObjectIdAndVersionedIn(
        processAreaObjectId,
        projectVersionQuery.data?.id
    );
    const userSystemRights = userActions.useGetUserEffectiveRightsOfUser(userId, projectId);
    const isProcessAdmin = Boolean(
        userSystemRights.data && userSystemRights.data.isProcessAdmin && !search.get('publishment')
    );

    const getVersionId = () => versionId ?? projectVersionQuery.data?.id;
    const getProcessAreaId = () => processAreaId ?? processAreaByObjectIdQuery.data?.id;

    const link = () =>
        projectVersionPublishmentQuery.data ?
            location.href.substring(0, location.href.indexOf('project')) +
            `project/${projectId}/published-process-area/${processAreaObjectId ?? processAreaByIdQuery.data?.objectId}?publishment=${encodeURIComponent(search.get('publishment') ?? projectVersionPublishmentQuery.data?.title ?? '')}`
        :   undefined;

    const getContent = () => {
        switch (selectedNode) {
            case MenuNodeTypes.ACTIVITIES:
                return (
                    <StackedActivitiesView
                        processAreaId={getProcessAreaId()}
                        versionId={getVersionId()}
                        isAdmin={isProcessAdmin}
                        link={link()}
                    />
                );

            case MenuNodeTypes.ENTRY_CRITERIA:
                return (
                    <StackedCriteriasView
                        processAreaId={getProcessAreaId()}
                        versionId={getVersionId()}
                        isAdmin={isProcessAdmin}
                        direction={CriteriaDirection.ENTER}
                        link={link()}
                    />
                );
            case MenuNodeTypes.OBJECTIVES:
                return (
                    <StackedObjectivesView
                        processAreaId={getProcessAreaId()}
                        versionId={getVersionId()}
                        isAdmin={isProcessAdmin}
                        link={link()}
                    />
                );
            case MenuNodeTypes.INPUTS:
                return (
                    <StackedProcessArtifactsView
                        processAreaId={getProcessAreaId()}
                        versionId={getVersionId()}
                        isAdmin={isProcessAdmin}
                        direction={ProcessArtifactDirection.INPUT}
                        link={link()}
                    />
                );
            case MenuNodeTypes.TOOLS:
                return (
                    <StackedToolsView
                        processAreaId={getProcessAreaId()}
                        versionId={getVersionId()}
                        isAdmin={isProcessAdmin}
                        link={link()}
                    />
                );
            case MenuNodeTypes.VERIFICATION_METRICS:
                return (
                    <StackedVerificationMetricsView
                        processAreaId={getProcessAreaId()}
                        versionId={getVersionId()}
                        isAdmin={isProcessAdmin}
                        link={link()}
                    />
                );
            case MenuNodeTypes.EXIT_CRITERIA:
                return (
                    <StackedCriteriasView
                        processAreaId={getProcessAreaId()}
                        versionId={getVersionId()}
                        isAdmin={isProcessAdmin}
                        direction={CriteriaDirection.EXIT}
                        link={link()}
                    />
                );
            case MenuNodeTypes.OUTPUTS:
                return (
                    <StackedProcessArtifactsView
                        processAreaId={getProcessAreaId()}
                        versionId={getVersionId()}
                        isAdmin={isProcessAdmin}
                        direction={ProcessArtifactDirection.OUTPUT}
                        link={link()}
                    />
                );
            case MenuNodeTypes.ROLES:
                return (
                    <StackedRolesView
                        processAreaId={getProcessAreaId()}
                        versionId={getVersionId()}
                        isAdmin={isProcessAdmin}
                        link={link()}
                    />
                );
            case MenuNodeTypes.METHODS:
                return (
                    <StackedMethodsView
                        processAreaId={getProcessAreaId()}
                        versionId={getVersionId()}
                        isAdmin={isProcessAdmin}
                        link={link()}
                    />
                );
            default:
                return;
        }
    };

    const selectNode = (type: MenuNodeTypes) => {
        const formatType = (identifier: MenuNodeTypes): string => {
            return identifier.replace(new RegExp('_', 'g'), '-').toLowerCase();
        };

        setSelectedNode(type);
        setSearch((prevParams) => {
            prevParams.set('section', formatType(type));
            return prevParams;
        });
    };

    return (
        <div style={{ width: '100%', height: '100%' }}>
            <ConditionalComponent label={'Please login to see Process Area'} condition={isAuthenticated}>
                <AppShell.Navbar p-top={'md'} style={{ width: '220px' }}>
                    <AppShell.Section grow>
                        <NavLink
                            label="Objectives"
                            active={selectedNode === MenuNodeTypes.OBJECTIVES}
                            onClick={() => selectNode(MenuNodeTypes.OBJECTIVES)}
                        />
                        <NavLink
                            label="Entry Criteria"
                            active={selectedNode === MenuNodeTypes.ENTRY_CRITERIA}
                            onClick={() => selectNode(MenuNodeTypes.ENTRY_CRITERIA)}
                        />
                        <NavLink
                            label="Inputs"
                            active={selectedNode === MenuNodeTypes.INPUTS}
                            onClick={() => selectNode(MenuNodeTypes.INPUTS)}
                        />
                        <NavLink
                            label="Outputs"
                            active={selectedNode === MenuNodeTypes.OUTPUTS}
                            onClick={() => selectNode(MenuNodeTypes.OUTPUTS)}
                        />
                        <NavLink
                            label="Roles"
                            active={selectedNode === MenuNodeTypes.ROLES}
                            onClick={() => selectNode(MenuNodeTypes.ROLES)}
                        />
                        <NavLink
                            label="Methods"
                            active={selectedNode === MenuNodeTypes.METHODS}
                            onClick={() => selectNode(MenuNodeTypes.METHODS)}
                        />
                        <NavLink
                            label="Activities"
                            active={selectedNode === MenuNodeTypes.ACTIVITIES}
                            onClick={() => selectNode(MenuNodeTypes.ACTIVITIES)}
                        />
                        <NavLink
                            label="Tools"
                            active={selectedNode === MenuNodeTypes.TOOLS}
                            onClick={() => selectNode(MenuNodeTypes.TOOLS)}
                        />
                        <NavLink
                            label="Verification Metrics"
                            active={selectedNode === MenuNodeTypes.VERIFICATION_METRICS}
                            onClick={() => selectNode(MenuNodeTypes.VERIFICATION_METRICS)}
                        />
                        <NavLink
                            label="Exit Criteria"
                            active={selectedNode === MenuNodeTypes.EXIT_CRITERIA}
                            onClick={() => selectNode(MenuNodeTypes.EXIT_CRITERIA)}
                        />
                    </AppShell.Section>

                    <Show when={isProcessAdmin}>
                        <AppShell.Section>
                            <Divider />
                            <NavLink
                                leftSection={<IconDownload />}
                                label="Download as Rich Text"
                                c="dimmed"
                                onClick={() => {
                                    OpenModal.AlertModal(
                                        'Process Area Rich Text File Download',
                                        'Do you want to download Process Area as a rich text?',
                                        () => {
                                            dispatch(setImportingOrExportingState(true));
                                            thunkDispatch(
                                                startDownloadRichText(
                                                    processAreaByIdQuery.data?.name ?? '',
                                                    processAreaId ?? '',
                                                    versionId ?? ''
                                                )
                                            ).then((value) => {
                                                if (value) {
                                                    dispatch(setTaskRefresh(true));
                                                }
                                                dispatch(setImportingOrExportingState(false));
                                            });
                                        }
                                    );
                                }}
                            />
                        </AppShell.Section>
                    </Show>
                </AppShell.Navbar>

                {getContent()}
            </ConditionalComponent>
        </div>
    );
};
